/**
 * One vertical's block in the every-vertical Ticket Pipeline (api#1136).
 *
 * The API sends every known status for the vertical, in pipeline order, at
 * zero where empty, each with its `stage`. This draws them as sent: nothing is
 * counted, summed or reordered here.
 *
 *   - MONEY PER CURRENCY, never summed across them. A status with no ticket is
 *     a real $0; tickets with no figure are "—", and a partial figure says
 *     "+ N unpriced" beside it, because a partial sum standing alone reads as
 *     the whole.
 *   - `money: 'not_stated'` (trucking BOLs carry no price): every amount is —,
 *     never $0.
 *   - A vertical that FAILED says so in place of its rows: never a pipeline of
 *     zeros. One that cannot be stated shows — and the API's reason.
 *
 * Casing's block is drawn by the casing pipeline component (TicketPipeline),
 * with the API's figures converted to its shape, so a casing tenant's rows look
 * and link exactly as before.
 */
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import TicketPipeline from '../dashboard/TicketPipeline';
import { fmtCurrency } from '../../lib/display-labels';
import { fmtAmountDigits } from '../../lib/currency-amounts';
import { pipelineStatusLabel, pipelineTitle, STAGE_FILL } from '../../lib/overview-ops-labels';
import type { TicketPipelineStatus } from '../../types/dashboard-layout';
import type { PipelineStatusRow, VerticalPipeline } from '../../types/overview-ops';

export function pipelineRowAmount(v: Pick<VerticalPipeline, 'money'>, s: PipelineStatusRow): { amount: string; note: string | null } {
  if (v.money === 'not_stated') return { amount: '—', note: null };
  if (s.count === 0) return { amount: fmtCurrency(0, null, 0), note: null };
  if (s.amounts.length === 0) return { amount: '—', note: null };
  const amount = s.amounts.map((a) => fmtAmountDigits(a, 0)).join(' · ');
  return { amount, note: s.unpriced_count > 0 ? `+ ${s.unpriced_count} unpriced` : null };
}

/** Casing's rows in the shape the casing component draws (one currency, the company's). */
export function asCasingStatuses(rows: PipelineStatusRow[]): TicketPipelineStatus[] {
  return rows.map((s) => ({
    status: s.status,
    count: s.count,
    priced_count: s.priced_count,
    priced_total: s.amounts.length === 0 ? null : s.amounts[0].amount,
    unpriced_count: s.unpriced_count,
  }));
}

function Rows({ v }: { v: VerticalPipeline }) {
  const max = Math.max(...v.statuses.map((s) => s.count), 1);
  return (
    <ul className="space-y-2" aria-label={`${v.label} by status`}>
      {v.statuses.map((s) => {
        const width = s.count > 0 ? Math.max((s.count / max) * 100, 12) : 0;
        const { amount, note } = pipelineRowAmount(v, s);
        return (
          <li key={s.status} data-testid={`pipeline-${v.vertical}-${s.status}`} className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-right text-[11px] font-semibold text-fieldiq-muted sm:w-32">
              {pipelineStatusLabel(v.vertical, s.status)}
            </span>
            <span className="relative h-7 flex-1 overflow-hidden rounded-fi-control bg-fieldiq-dark/60">
              {s.count > 0 && (
                <span
                  className={`absolute inset-y-0 left-0 rounded-fi-control ${STAGE_FILL[s.stage] ?? 'bg-fi-signal-slate'}`}
                  style={{ width: `max(${width}%, 2.5rem)` }}
                  aria-hidden="true"
                />
              )}
              <span
                data-testid="pipeline-count"
                className={`absolute inset-y-0 left-2 flex items-center text-sm font-extrabold tabular-nums ${s.count > 0 ? 'text-fi-signal-ink' : 'text-fieldiq-muted'}`}
              >
                {s.count}
              </span>
            </span>
            <span className="w-28 shrink-0 text-right">
              <span data-testid="pipeline-amount" className="block text-xs font-bold tabular-nums text-fieldiq-text">{amount}</span>
              {note && <span className="block text-[10px] text-fieldiq-muted">{note}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function VerticalPipelineBlock({ v }: { v: VerticalPipeline }) {
  const navigate = useNavigate();
  const title = v.status === 'failed' ? pipelineTitle(v.vertical) : v.label || pipelineTitle(v.vertical);
  const empty = v.statuses.length > 0 && v.statuses.every((s) => s.count === 0);

  let body: React.ReactNode;
  if (v.status === 'failed') {
    body = <p data-testid="pipeline-failed" className="text-xs text-fieldiq-muted">Couldn’t load these. This is not an empty pipeline.</p>;
  } else if (v.status === 'not_computable') {
    body = <p data-testid="pipeline-not-computable" className="text-xs text-fieldiq-muted">—{v.reason ? ` ${v.reason}` : ''}</p>;
  } else if (empty) {
    body = <p data-testid="pipeline-vertical-empty" className="text-xs text-fieldiq-muted">No tickets yet.</p>;
  } else if (v.vertical === 'casing_running') {
    body = <TicketPipeline statuses={asCasingStatuses(v.statuses)} />;
  } else {
    body = <Rows v={v} />;
  }

  return (
    <section data-testid={`pipeline-block-${v.vertical}`} className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-wide text-fieldiq-text">{title}</h3>
        {v.link && v.status !== 'failed' && (
          <button
            type="button"
            onClick={() => navigate(v.link)}
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4"
          >
            Open <ChevronRight className="size-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {body}
    </section>
  );
}
